import * as Styled from './CreateOrganization.styled';
import OrganizationIcon from '../../../../assets/icons/OrganizationIcon.svg';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import {
  useCreateOrganization,
} from '@modules/onboarding/accountOnboarding/pages/organization/components/CreateOrganizationPage/api/useCreateOrganization';
import { useRegions } from '@modules/workspaces/hooks/useRegions';
import { useUser } from 'app/_providers/User';
import { ORGANIZATION_NAME_MAX_LENGTH } from 'app/_utils/data/organization';

import { Toast } from '@capra/core';
import { Form, Input, Select, Row, Col } from 'antd';
import { Route } from 'next';
import { useRouter } from 'next/navigation';
import * as React from 'react';
import { ORGANIZATION_ALIAS_RULES } from 'shared/src/organization/constants';

type MarketplaceType = 'aws';

type MarketplaceInputType = {
  type: MarketplaceType;
  customerId: string;
  productId: string;
};

type NewOrgInput = {
  region: string;
  organizationAlias?: string;
  marketplace?: MarketplaceInputType;
};

const ModalTitle = () => (
  <Row align='middle'>
    <Col>
      <Styled.IconStyle>
        <OrganizationIcon />
      </Styled.IconStyle>
    </Col>

    <Col>
      <Row>
        <Styled.OrgHeaderSecondary>Organization</Styled.OrgHeaderSecondary>
      </Row>

      <Row>
        <Styled.NewOrgTitle>
          New Organization
        </Styled.NewOrgTitle>
      </Row>
    </Col>
  </Row>
);

const OrganizationNameTitle = () => (
  <>
    Organization Name

    <Styled.OptionalText>&nbsp;(optional)</Styled.OptionalText>
  </>
);

interface CreateOrganizationProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateOrganization: React.FC<CreateOrganizationProps> = ({ isOpen, onClose }) => {
  const { cloudAwsMarketplaceEnabled } = useFlagsProxy();
  const { regionOptions } = useRegions();
  const { user, getAccessToken } = useCommonAuthProvider();
  const [marketplaceInput, setMarketplaceInput] = React.useState<MarketplaceInputType>();
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [orgNameCharCount, setOrgNameCharCount] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isSubmittingRef = React.useRef(false);
  const isOnOkInProgressRef = React.useRef(false);
  const { trigger, isMutating } = useCreateOrganization();
  const isBusy = isMutating || isSubmitting;
  const router = useRouter();
  const { memberships } = useUser();

  React.useEffect(() => {
    if (cloudAwsMarketplaceEnabled && user) {
      const marketplaceInputBase = {
        type: user[`${process.env.AUTH_CLIENT_CUSTOM_CLAIM}/marketplaceType`],
        customerId: user[`${process.env.AUTH_CLIENT_CUSTOM_CLAIM}/marketplaceCustomerId`],
        productId: user[`${process.env.AUTH_CLIENT_CUSTOM_CLAIM}/marketplaceProductId`],
      };
      setMarketplaceInput(marketplaceInputBase);
    }
  }, [user, cloudAwsMarketplaceEnabled]);

  const handleFormValuesChange = (): void => {
    const region = form.getFieldValue('region');
    const organizationAlias = form.getFieldValue('organizationAlias') || '';
    setOrgNameCharCount(organizationAlias.length);
    setIsFormValid(!!region);
  };

  const handleCreate = async (
    values: NewOrgInput = {
      region: '',
    },
  ): Promise<unknown> => {
    if (isSubmittingRef.current) {
      return null;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (cloudAwsMarketplaceEnabled && marketplaceInput?.customerId) {
        values.marketplace = marketplaceInput;
      }
      const response = await trigger(values);
      // forcefully get a new token, so you have updated claim for organizations
      await getAccessToken({ cacheMode: 'off' });
      await memberships.mutate();

      router.push(response.data.location as Route);

      return null;
    } catch (e: any) {
      Toast.error(e.info?.message || 'Unable to create Organization', { position: 'bottom-right' });
      isSubmittingRef.current = false;
      isOnOkInProgressRef.current = false;
      setIsSubmitting(false);

      return null;
    }
  };

  return (
    <Styled.ModalGlobalStyle
      title={<ModalTitle />}
      onOk={() => {
        if (isOnOkInProgressRef.current) {
          return Promise.resolve();
        }

        isOnOkInProgressRef.current = true;

        return form
          .validateFields()
          .then((values) => handleCreate(values as NewOrgInput))
          .catch((err) => {
            isOnOkInProgressRef.current = false;
            throw err;
          });
      }}
      open={isOpen}
      onCancel={onClose}
      okText={isBusy ? 'Saving' : 'Save'}
      okButtonProps={{
        disabled: isBusy || !isFormValid,
        type: 'primary',
        ghost: isBusy,
        htmlType: 'button',
      }}
      cancelText='Cancel'
      maskClosable={false}
    >
      <Form
        layout='vertical'
        onValuesChange={handleFormValuesChange}
        form={form}
        initialValues={{ organizationAlias: '' }}
      >
        <Styled.FormInput>
          <Form.Item
            label={<OrganizationNameTitle />}
            name='organizationAlias'
            rules={[{
              required: false,
              pattern: ORGANIZATION_ALIAS_RULES.regex,
              message: ORGANIZATION_ALIAS_RULES.invalidMessage,
            }]}
          >
            <Input
              disabled={isBusy}
              data-testid='create-org-name'
              maxLength={ORGANIZATION_NAME_MAX_LENGTH}
              suffix={
                <Styled.CharCountSuffix>
                  {orgNameCharCount}/{ORGANIZATION_NAME_MAX_LENGTH}
                </Styled.CharCountSuffix>
              }
            />
          </Form.Item>
        </Styled.FormInput>

        <Form.Item>
          <Styled.InfoMessage>{ORGANIZATION_ALIAS_RULES.message}</Styled.InfoMessage>
        </Form.Item>

        <Form.Item
          label='Region'
          name='region'
          required
        >
          <Select disabled={isBusy}>
            {regionOptions.map(({ value, label }) => (
              <Select.Option
                key={value}
                value={value}
              >
                {label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Styled.ModalGlobalStyle>
  );
};
